/**
 * Real User Monitoring (RUM) — Djayma storefront.
 *
 * Tracks:
 * - Web Vitals (LCP, FID, CLS, TTFB, INP) via the `web-vitals` package.
 * - API call timing (every fetch duration, endpoint, status code).
 * - Navigation timing (time to first meaningful paint per route).
 * - Uncaught error rate per page.
 * - Checkout funnel drop-off (see checkout-funnel.ts).
 *
 * Metrics are sent to:
 * 1. Sentry performance monitoring (when DSN is set).
 * 2. /api/metrics endpoint on the distribution-api.
 *
 * No PII is collected — Senegal Loi 2008-12.
 */

import type { Metric } from 'web-vitals';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const API_URL = import.meta.env.VITE_API_URL ?? '';
const METRICS_ENDPOINT = `${API_URL}/api/metrics`;
const IS_PRODUCTION = import.meta.env.MODE === 'production';

// Batch metrics to avoid flooding the endpoint
const FLUSH_INTERVAL_MS = 15_000;
const MAX_BATCH_SIZE = 30;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RumMetric {
  readonly name: string;
  readonly value: number;
  readonly unit: 'ms' | 'score' | 'count' | 'bytes';
  readonly tags: Readonly<Record<string, string>>;
  readonly timestamp: string;
}

export interface RumConfig {
  environment: string;
  /** Sampling rate 0–1. Default: 1.0 in dev, 0.5 in prod. */
  sampleRate?: number;
  /** Override the metrics endpoint URL. */
  metricsEndpoint?: string;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let _config: Required<RumConfig> | null = null;
let _buffer: RumMetric[] = [];
let _flushTimer: ReturnType<typeof setInterval> | null = null;
let _errorCount = 0;
let _pageLoadStart = performance.now();

// ---------------------------------------------------------------------------
// Core
// ---------------------------------------------------------------------------

function shouldSample(): boolean {
  if (!_config) return false;
  return Math.random() < _config.sampleRate;
}

export function recordMetric(metric: RumMetric): void {
  if (!_config || !shouldSample()) return;

  _buffer = [..._buffer, metric];

  if (_buffer.length >= MAX_BATCH_SIZE) {
    flush();
  }
}

function makeMetric(
  name: string,
  value: number,
  unit: RumMetric['unit'],
  tags: Record<string, string> = {},
): RumMetric {
  return {
    name,
    value,
    unit,
    tags: {
      environment: _config?.environment ?? 'unknown',
      route: window.location.pathname,
      ...tags,
    },
    timestamp: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Flush
// ---------------------------------------------------------------------------

async function flush(): Promise<void> {
  if (_buffer.length === 0) return;

  const batch = [..._buffer];
  _buffer = [];

  try {
    await fetch(METRICS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ metrics: batch }),
      // keepalive = true allows flush during page unload
      keepalive: true,
    });
  } catch {
    // Network failure — discard batch (non-critical telemetry path)
  }
}

// ---------------------------------------------------------------------------
// Web Vitals
// ---------------------------------------------------------------------------

async function initWebVitals(): Promise<void> {
  try {
    const { onCLS, onFID, onLCP, onTTFB, onINP } = await import('web-vitals');

    const report = (metric: Metric): void => {
      // Send to Sentry performance monitoring (fire-and-forget)
      if (import.meta.env.VITE_SENTRY_DSN) {
        import('@/lib/sentry')
          .then(({ Sentry }) => {
            // Sentry.metrics is available in Sentry SDK v7.x performance monitoring
            const sentryWithMetrics = Sentry as unknown as {
              metrics?: { distribution: (n: string, v: number, o: object) => void };
            };
            sentryWithMetrics.metrics?.distribution(metric.name, metric.value, {
              tags: { rating: metric.rating },
            });
          })
          .catch(() => {
            // Sentry not available — continue
          });
      }

      // Buffer for /api/metrics
      recordMetric(
        makeMetric(
          `web_vitals.${metric.name.toLowerCase()}`,
          metric.value,
          metric.name === 'CLS' ? 'score' : 'ms',
          { rating: metric.rating, id: metric.id },
        ),
      );
    };

    onCLS(report);
    onFID(report);
    onLCP(report);
    onTTFB(report);
    onINP(report);
  } catch {
    // web-vitals package not installed — skip
    if (!IS_PRODUCTION) {
      console.warn('[RUM] web-vitals package not available. Run: pnpm add web-vitals');
    }
  }
}

// ---------------------------------------------------------------------------
// API call timing (fetch monkey-patch)
// ---------------------------------------------------------------------------

function initFetchTiming(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async function trackedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : (input as Request).url;

    // Only track calls to our own API
    if (!url.startsWith(API_URL) || !API_URL) {
      return originalFetch(input, init);
    }

    const start = performance.now();
    let status = 0;

    try {
      const response = await originalFetch(input, init);
      status = response.status;
      return response;
    } catch (error: unknown) {
      status = 0; // network failure
      throw error;
    } finally {
      const duration = performance.now() - start;
      const endpoint = url.replace(API_URL, '').split('?')[0]; // strip query params

      recordMetric(
        makeMetric('api.call.duration', duration, 'ms', {
          endpoint,
          method: (init?.method ?? 'GET').toUpperCase(),
          status: String(status),
          success: status >= 200 && status < 400 ? 'true' : 'false',
        }),
      );
    }
  };
}

// ---------------------------------------------------------------------------
// Navigation timing (route change)
// ---------------------------------------------------------------------------

export function trackRouteChange(route: string): void {
  const now = performance.now();
  const duration = now - _pageLoadStart;
  _pageLoadStart = now;

  recordMetric(makeMetric('navigation.route_change', duration, 'ms', { route }));
}

export function trackPageLoad(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('load', () => {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (!nav) return;

    recordMetric(makeMetric('navigation.page_load', nav.loadEventEnd - nav.startTime, 'ms'));
    recordMetric(makeMetric('navigation.dom_interactive', nav.domInteractive - nav.startTime, 'ms'));
    recordMetric(makeMetric('navigation.ttfb', nav.responseStart - nav.requestStart, 'ms'));
  });
}

// ---------------------------------------------------------------------------
// Error rate tracking
// ---------------------------------------------------------------------------

function initErrorTracking(): void {
  window.addEventListener('error', () => {
    _errorCount += 1;
    recordMetric(makeMetric('errors.uncaught', _errorCount, 'count'));
  });

  window.addEventListener('unhandledrejection', () => {
    _errorCount += 1;
    recordMetric(makeMetric('errors.unhandled_promise', _errorCount, 'count'));
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize RUM. Call once before createRoot.
 */
export async function initRUM(config: RumConfig): Promise<void> {
  if (_config) return; // already initialized

  _config = {
    environment: config.environment,
    sampleRate: config.sampleRate ?? (IS_PRODUCTION ? 0.5 : 1.0),
    metricsEndpoint: config.metricsEndpoint ?? METRICS_ENDPOINT,
  };

  // Start periodic flush
  if (_flushTimer) clearInterval(_flushTimer);
  _flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  // Flush on page hide (covers tab close, navigation, background)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });

  // Initialize all collectors
  initFetchTiming();
  initErrorTracking();
  trackPageLoad();
  await initWebVitals();
}

export { flush as flushRUM };
